<template>
  <div class="position-relative">
    <div class="add-entity-action d-flex align-center justify-end">
      <help-dialog-workflow-entities />
      <v-btn
        height="42"
        class="px-5"
        @click="openEntityDialog = true"
        :disabled="store.state.isWorkflowDisabled"
      >
        <v-icon>mdi-plus</v-icon>
        Entity
      </v-btn>
      <create-entity-dialog v-model="openEntityDialog" />
    </div>
    <div class="mt-10" ref="entitiesList">
      <div v-for="(entity, index) in entities" :key="entity.id" class="entity-sortable-item">
        <beam-table
          :schema="entity"
          :schema-index="index"
          :show-entity-drag-handle="true"
          :usage-status="entityUsageStatus.get(entity.id)"
          @blur-schema-name="(e) => blurEntityName(e, entity.id)"
          @delete-schema="setShowDeleteDialog('entity', { id: entity.id })"
          @blur-table-cell="blurTableCell"
          @add-column="addColumn"
          @delete-field="(schemaId, fieldIndex) => setShowDeleteDialog('entity-field', { schemaId, fieldIndex })"
          @move-left="moveLeft"
          @move-right="moveRight"
          @generate-example-data="generateExampleData"
          @delete-example-row="(schemaId, rowIndex) => deleteExampleDataRow(schemaId, rowIndex)"
          :rows="[
            {
              type: 'header',
              fieldName: 'name',
              rowLabel: 'Entity'
            },
            {
              type: 'relation',
              fieldName: 'relatedEntityId'
            },
            {
              type: 'text',
              fieldName: 'description',
              rowLabel: 'Description'
            },
            {
              type: 'example-data',
              fieldName: 'exampleData'
            }
          ]"
        />
      </div>
      <v-card v-if="!entities?.length" flat color="white" class="mb-4 elevation-2 rounded px-3 py-3 border-0">
        <div class="d-flex align-center mb-6">
          No&nbsp;<span class="main-badge">Entities</span>&nbsp;created yet.
          <help-dialog-workflow-entities />
        </div>
        <div>
          Click on <span class="main-badge">+ Entity</span> to create an entity.
        </div>
      </v-card>
    </div>

    <delete-confirmation-dialog
      v-model="openDeleteDialog"
      :title="dialogInfo.title"
      :text="dialogInfo.text"
      :onDelete="dialogInfo.action"
    />
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import Sortable from 'sortablejs'
import BeamTable from '@/components/BeamTable.vue'
import CreateEntityDialog from '@/components/CreateEntityDialog.vue'
import DeleteConfirmationDialog from '@/components/DeleteConfirmationDialog.vue'
import useEntityActions from '@/composables/useEntityActions'
import HelpDialogWorkflowEntities from '@/components/HelpDialogWorkflowEntities.vue'
import { SchemaType } from '@/constants/schema'
import { DomainModelRole } from '@/constants/cardTypes'
import workflowApi from '@/api/workflowApi'
import safeStructuredClone from '@/utils/clone'

const store = useStore()
const workflow = computed(() => store.state.workflow.workflow)
const isWorkflowDisabled = computed(() => store.state.isWorkflowDisabled)
const entities = computed(() => {
  return (workflow.value?.schemas ?? []).filter((schema) => schema.type === SchemaType.ENTITY)
})

const entityUsageStatus = computed(() => {
  const domainModelEntityIds = new Set()
  const domainModelSchemaIds = new Set()
  const referencedInDomainModel = new Set()
  const referencedByUnusedEntities = new Set()

  const domainEvents = workflow.value?.domainEvents ?? []
  const schemas = workflow.value?.schemas ?? []
  const schemasById = new Map(schemas.map((s) => [s.id, s]))

  domainEvents.forEach((event) => {
    event.cards?.forEach((card) => {
      if (!card.schemaId) return
      const role = card.cardType?.domainModelRole

      if (role === DomainModelRole.AGGREGATE_ROOT) {
        domainModelEntityIds.add(card.schemaId)
      } else if (role === DomainModelRole.READ_MODEL) {
        domainModelSchemaIds.add(card.schemaId)
        const querySchema = schemasById.get(card.schemaId)
        if (querySchema?.entityId) {
          domainModelEntityIds.add(querySchema.entityId)
        }
      } else if (role === DomainModelRole.COMMAND) {
        domainModelSchemaIds.add(card.schemaId)
      }
    })
  })

  const entityFieldRefs = new Map()
  schemas.forEach((schema) => {
    if (domainModelSchemaIds.has(schema.id) || domainModelEntityIds.has(schema.id)) {
      schema.fields?.forEach((field) => {
        if (field.relatedEntityId) {
          referencedInDomainModel.add(field.relatedEntityId)
        }
      })
    }
    if (schema.type === SchemaType.ENTITY && schema.fields?.length) {
      const refs = schema.fields.filter((f) => f.relatedEntityId).map((f) => f.relatedEntityId)
      if (refs.length) entityFieldRefs.set(schema.id, refs)
    }
  })

  referencedInDomainModel.forEach((entityId) => {
    entityFieldRefs.get(entityId)?.forEach((ref) => referencedInDomainModel.add(ref))
  })

  entityFieldRefs.forEach((refs, entityId) => {
    const isUsed = domainModelEntityIds.has(entityId) || referencedInDomainModel.has(entityId)
    if (!isUsed) refs.forEach((ref) => referencedByUnusedEntities.add(ref))
  })

  const statusMap = new Map()
  entities.value.forEach((entity) => {
    const isUsedDirectly = domainModelEntityIds.has(entity.id) || referencedInDomainModel.has(entity.id)
    if (isUsedDirectly) {
      return
    }
    if (referencedByUnusedEntities.has(entity.id)) {
      statusMap.set(entity.id, 'only-referenced')
    } else {
      statusMap.set(entity.id, 'unused')
    }
  })

  return statusMap
})

const entitiesList = ref(null)
const sortable = ref(null)

const openEntityDialog = ref(false)
const openDeleteDialog = ref(false)
const dialogInfo = ref({ title: '', text: '', action: null })

const {
  moveRight,
  moveLeft,
  blurEntityName,
  blurTableCell,
  addColumn,
  deleteEntity,
  deleteEntityField,
  generateExampleData,
  deleteExampleDataRow
} = useEntityActions()

const reorderEntities = async (fromIndex, toIndex) => {
  if (!workflow.value?.schemas) {
    return
  }

  const newSchemas = safeStructuredClone(workflow.value.schemas)
  const entitySchemas = newSchemas.filter((schema) => schema.type === SchemaType.ENTITY)

  if (!entitySchemas[fromIndex]) {
    return
  }

  const [movedEntity] = entitySchemas.splice(fromIndex, 1)
  entitySchemas.splice(toIndex, 0, movedEntity)

  const reorderedSchemas = newSchemas.map((schema) => {
    if (schema.type !== SchemaType.ENTITY) {
      return schema
    }

    return entitySchemas.shift()
  })

  await workflowApi.updateSchemas({
    schemas: reorderedSchemas,
    workflow: workflow.value
  })
}

const initSortable = () => {
  if (sortable.value) {
    sortable.value.destroy()
    sortable.value = null
  }

  if (isWorkflowDisabled.value || !entitiesList.value) {
    return
  }

  sortable.value = new Sortable(entitiesList.value, {
    draggable: '.entity-sortable-item',
    handle: '.entity-sort-handle',
    ghostClass: 'sortable-ghost',
    chosenClass: 'sortable-chosen',
    dragClass: 'sortable-drag',
    animation: 150,
    scroll: true,
    forceFallback: false,
    scrollSensitivity: 100,
    onEnd: async (event) => {
      const { oldIndex, newIndex } = event
      if (oldIndex === undefined || newIndex === undefined || oldIndex === newIndex) {
        return
      }

      await reorderEntities(oldIndex, newIndex)
    }
  })
}

onMounted(async () => {
  await nextTick()
  initSortable()
})

onBeforeUnmount(() => {
  if (sortable.value) {
    sortable.value.destroy()
    sortable.value = null
  }
})

watch(
  () => [entities.value.map((entity) => entity.id).join(','), isWorkflowDisabled.value],
  async () => {
    await nextTick()
    initSortable()
  }
)

const setShowDeleteDialog = (type, data) => {
  openDeleteDialog.value = true

  switch (type) {
    case 'entity':
      dialogInfo.value = {
        title: 'Confirm data model deletion',
        text: 'Are you sure you want to delete this data model entity?',
        action: () => deleteEntity(data.id)
      }
      break
    case 'entity-field':
      dialogInfo.value = {
        title: 'Confirm field deletion',
        text: 'Are you sure you want to delete this field?',
        action: () => deleteEntityField(data.schemaId, data.fieldIndex)
      }
      break
  }
}

</script>

<style scoped>
.add-entity-action {
  margin-top: -40px;
}
</style>
